import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import {
  RiArrowRightSLine,
  RiAddLine,
  RiDeleteBin6Line,
  RiUserAddLine,
  // TODO(account-hide): 成員權限（toggle / 角色預設 / 分類）尚未接 API，整段暫時隱藏。
  // 對應 import：RiFireLine, RiBillLine, RiTeamLine, RiSettings3Line, RiCheckLine, RiShieldUserLine,
  //              RiExchangeLine（負責人轉讓）。恢復時連同下方註解區塊一起取消註解。詳見 docs/account-hidden-sections.md
} from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import appToast from "@components/Toast";
import { colors } from "@styles/colors";
import { IdentityContext } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import FnRow from "./components/ui/FnRow";
import InfoNote from "./components/ui/InfoNote";
import BottomSheet from "./components/ui/BottomSheet";
import ToggleRow from "./components/ui/ToggleRow";
import {
  handleAPIKickMember,
  handleAPIListContextMembers,
  handleAPIApproveMember,
  handleAPIRejectMember,
  handleAPIGetGuestJoinSetting,
  handleAPIUpdateGuestJoinSetting,
  type ContextMember,
} from "./API/handleContextAPI";
import { track } from "@lib/tracker";

/* =====================================================================
 * TODO(account-hide): 成員權限（角色預設 / 分類權限 / 行內開關）
 * 此區塊為「成員權限」切版，尚未接任何 API，依需求暫時隱藏；程式碼保留待實作後恢復。
 * 詳見 docs/account-hidden-sections.md
 * ---------------------------------------------------------------------
 * interface PermDef { id: string; label: string; desc: string; }
 * interface PermCategory { id: string; label: string; icon: ReactNode; color: string; perms: PermDef[]; }
 * interface RolePreset { label: string; desc: string; permIds: string[]; }
 *
 * const PERM_CATEGORIES: PermCategory[] = [ ...叫瓦斯 / 帳務付款 / 成員管理 / 帳戶設定 ... ];
 * const ALL_PERM_IDS = PERM_CATEGORIES.flatMap((c) => c.perms.map((p) => p.id));
 * const ROLE_PRESETS: Record<string, RolePreset> = { owner / manager / member / viewer };
 * const ORDERED_PRESET_KEYS = ["owner", "manager", "member", "viewer"];
 * const presetKeyForIndex = (index) => ...循環指派預設角色...;
 * function PermToggle({ value, accentColor, onPress }) { ...42x24 軌道開關... }
 *
 * 連同元件內的 enabledByMember / presetByMember 狀態與
 * getEnabled / getPresetKey / enabledCount / togglePerm / applyPreset，以及
 * 「快速套用角色」「各分類權限」「角色說明」「負責人轉讓」JSX 一併恢復。
 * ===================================================================== */

interface MemberItem {
  customer_id?: number;
  customer_name: string;
  role?: "owner" | "member";
  color?: string;
}

/** 角色標籤文字（owner → 負責人；其餘 → 成員） */
const roleLabel = (m: MemberItem): string => (m.role === "owner" ? "負責人" : "成員");
const isOwner = (m: MemberItem): boolean => m.role === "owner";

interface AvatarProps {
  name: string;
  color?: string;
  size?: number;
}

/** 圓形頭像（取名稱首字） */
function Avatar({ name, color = colors.primary, size = 36 }: AvatarProps) {
  return (
    <View w={size} h={size} br={size / 2} bg={`${color}25`} ai="center" jc="center" flexShrink={0}>
      <Text fontSize={size * 0.4} fontWeight="800" color={color}>
        {name.charAt(0) || "?"}
      </Text>
    </View>
  );
}

interface RoleTagProps {
  label: string;
  tone?: "amber" | "primary";
}

function RoleTag({ label, tone = "primary" }: RoleTagProps) {
  const bg = tone === "amber" ? colors.bgAmber : colors.primaryBg;
  const fg = tone === "amber" ? colors.amberDark : colors.primaryText;
  return (
    <View br={10} px="$2" py="$0.5" bg={bg} flexShrink={0}>
      <Text fontSize={10} fontWeight="700" color={fg}>
        {label}
      </Text>
    </View>
  );
}

/**
 * 瓦斯行帳戶「成員管理」頁。
 * 成員資料來自 router state 的 IdentityContext.members；可查看成員清單、邀請新成員、
 * 移除非負責人成員（DELETE /customer/contexts/:id/members/:customer_id）。
 *
 * 負責人另外看得到「申請加入」（付款連結付完款申請加入的人，可核准／拒絕）與「陌生訪客自動加入」開關（2026-10-28）。
 *
 * 註：成員「權限」相關 UI（角色預設 / 分類權限開關）尚未接 API，暫時隱藏，見上方 TODO(account-hide)。
 */
export const MemberPermissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const groupName = ctx?.label || ctx?.context_name || "瓦斯行帳戶";
  // 只有負責人（owner）能移除成員；非 owner 連按鈕都不顯示（後端亦會擋）
  const viewerIsOwner = ctx?.role === "owner";

  const [members, setMembers] = useState<MemberItem[]>(() => ctx?.members ?? []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);

  // 負責人才有：付款連結付完款申請加入的人（invited）與「陌生訪客自動加入」設定（2026-10-28）
  const [pending, setPending] = useState<ContextMember[]>([]);
  const [guestAutoJoin, setGuestAutoJoin] = useState<boolean | null>(null);
  const [savingGuestJoin, setSavingGuestJoin] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ContextMember | null>(null);

  // 重抓申請中與目前成員：核准／拒絕後兩邊都會變
  const reloadMembers = useCallback(async () => {
    if (!viewerIsOwner || !contextId || !cusJwtToken) return;
    const [inv, act] = await Promise.all([
      handleAPIListContextMembers(contextId, cusJwtToken, "invited"),
      handleAPIListContextMembers(contextId, cusJwtToken, "active"),
    ]);
    if (inv.state === "success") setPending(inv.data);
    if (act.state === "success") {
      setMembers((prev) =>
        act.data.map((m) => ({
          customer_id: m.customer_id,
          customer_name: m.customer_name,
          role: m.role === "owner" ? "owner" : "member",
          // 頭像顏色沿用原本那筆（列表 API 沒有顏色）
          color: prev.find((p) => p.customer_id === m.customer_id)?.color,
        })),
      );
    }
  }, [viewerIsOwner, contextId, cusJwtToken]);

  useEffect(() => {
    if (!viewerIsOwner || !contextId || !cusJwtToken) return undefined;
    let alive = true;
    (async () => {
      const [inv, setting] = await Promise.all([
        handleAPIListContextMembers(contextId, cusJwtToken, "invited"),
        handleAPIGetGuestJoinSetting(contextId, cusJwtToken),
      ]);
      if (!alive) return;
      if (inv.state === "success") setPending(inv.data);
      // 讀不到（例如舊後端）就不顯示這個開關
      if (setting.state === "success") setGuestAutoJoin(setting.data.guest_auto_join);
    })();
    return () => {
      alive = false;
    };
  }, [viewerIsOwner, contextId, cusJwtToken]);

  const toggleGuestAutoJoin = async (next: boolean) => {
    if (!contextId || !cusJwtToken || savingGuestJoin) return;
    setSavingGuestJoin(true);
    const prev = guestAutoJoin;
    setGuestAutoJoin(next);
    const r = await handleAPIUpdateGuestJoinSetting(contextId, next, cusJwtToken);
    setSavingGuestJoin(false);
    if (r.state !== "success") {
      setGuestAutoJoin(prev);
      appToast.error("設定沒有存成功，請稍後再試");
      return;
    }
    setGuestAutoJoin(r.data.guest_auto_join);
  };

  const approve = async (m: ContextMember) => {
    if (!contextId || !cusJwtToken || actingId !== null) return;
    setActingId(m.customer_id);
    const r = await handleAPIApproveMember(contextId, m.customer_id, cusJwtToken);
    if (r.state !== "success") {
      setActingId(null);
      appToast.error("核准失敗，請稍後再試");
      return;
    }
    appToast.success(`已核准「${m.customer_name}」加入`);
    await reloadMembers();
    setActingId(null);
  };

  const closeReject = () => {
    if (actingId !== null) return;
    setRejectTarget(null);
  };

  const confirmReject = async () => {
    if (!rejectTarget || !contextId || !cusJwtToken || actingId !== null) return;
    const m = rejectTarget;
    setActingId(m.customer_id);
    const r = await handleAPIRejectMember(contextId, m.customer_id, cusJwtToken);
    if (r.state !== "success") {
      setActingId(null);
      appToast.error("拒絕失敗，請稍後再試");
      return;
    }
    appToast.success(`已拒絕「${m.customer_name}」的申請`);
    await reloadMembers();
    setActingId(null);
    setRejectTarget(null);
  };

  const back = () => {
    if (editingIndex !== null) {
      setEditingIndex(null);
      return;
    }
    // 帶回更新後的成員清單，讓帳戶詳情頁的成員數同步
    const nextCtx = ctx ? { ...ctx, members, member_count: members.length } : ctx;
    navigate(`/account/detail/${contextId}`, { state: { context: nextCtx } });
  };

  const goInvite = () => navigate(`/account/detail/${contextId}/invite`, { state: { context: ctx } });

  const closeRemove = () => {
    if (removing) return;
    setRemoveTarget(null);
  };

  const confirmRemove = async () => {
    if (removeTarget === null) return;
    const target = members[removeTarget];
    if (!target?.customer_id) {
      appToast.error("找不到成員資料，請重新整理後再試");
      return;
    }
    if (!cusJwtToken) {
      appToast.error("請重新登入後再試");
      return;
    }
    if (!contextId) return;

    track("member_remove_click", {});
    setRemoving(true);
    const result = await handleAPIKickMember(contextId, target.customer_id, cusJwtToken);
    setRemoving(false);

    if (result.state !== "success") {
      appToast.error(result.error || "移除失敗");
      return;
    }
    appToast.success(`已移除「${target.customer_name}」`);
    setMembers((prev) => prev.filter((_, i) => i !== removeTarget));
    setRemoveTarget(null);
    setEditingIndex(null);
  };

  const editing = editingIndex !== null ? members[editingIndex] : null;
  const removingMember = removeTarget !== null ? members[removeTarget] : null;

  /* 標題由釘住的標題列負責，淡霧頁首上只留一行說明（進到單一成員時換成那個人的脈絡）。 */
  const subHeader = (
    <YStack mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        {editing ? `「${editing.customer_name}」在 ${groupName} 的成員資料` : `管理「${groupName}」的成員`}
      </Text>
    </YStack>
  );

  return (
    <SkinV2Screen title="成員管理" onBack={back} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$4">
        {editingIndex === null ? (
          <>
            {/* 申請加入（只有負責人看得到、有申請才顯示）：付款連結付完款申請加入的人 */}
            {viewerIsOwner && pending.length > 0 && (
              <YStack>
                <SectionLabel tone="dark" title={`申請加入 (${pending.length})`} />
                <ListContainer>
                  {pending.map((m, i) => {
                    const busy = actingId === m.customer_id;
                    const locked = actingId !== null;
                    return (
                      <XStack
                        key={m.customer_id}
                        ai="center"
                        gap="$3"
                        px="$3.5"
                        py="$3"
                        bbw={i === pending.length - 1 ? 0 : 1}
                        bbc={v2.divider}>
                        <Avatar name={m.customer_name} />
                        <YStack f={1} miw={0} gap="$1">
                          <Text fontSize={14} fontWeight="700" color={v2.title} numberOfLines={1}>
                            {m.customer_name}
                          </Text>
                          {m.is_payer && (
                            <XStack>
                              <RoleTag label="申請付款權限" tone="amber" />
                            </XStack>
                          )}
                        </YStack>
                        <XStack
                          ai="center"
                          jc="center"
                          px="$3"
                          py="$2"
                          br={999}
                          bg={v2.chipBg}
                          cursor={locked ? "default" : "pointer"}
                          opacity={locked ? 0.5 : 1}
                          pressStyle={{ opacity: 0.7 }}
                          role="button"
                          aria-label={`拒絕「${m.customer_name}」`}
                          onPress={locked ? undefined : () => setRejectTarget(m)}>
                          <Text fontSize={13} fontWeight="700" color={v2.sub}>
                            拒絕
                          </Text>
                        </XStack>
                        <XStack
                          ai="center"
                          jc="center"
                          px="$3"
                          py="$2"
                          br={999}
                          bg={v2.primary}
                          cursor={locked ? "default" : "pointer"}
                          opacity={locked && !busy ? 0.5 : 1}
                          pressStyle={{ opacity: 0.85 }}
                          role="button"
                          aria-label={`核准「${m.customer_name}」`}
                          onPress={locked ? undefined : () => approve(m)}>
                          <Text fontSize={13} fontWeight="700" color={colors.light}>
                            {busy && !rejectTarget ? "處理中…" : "核准"}
                          </Text>
                        </XStack>
                      </XStack>
                    );
                  })}
                </ListContainer>
              </YStack>
            )}

            {/* 成員清單 */}
            <YStack>
              {/* 頁首是淡霧底（見 useHazePage），這行用一般深色即可 */}
              <SectionLabel tone="dark" title={`成員 (${members.length})`} />
              <ListContainer>
                {members.length === 0 ? (
                  <Text fontSize={13} color={colors.hint} ta="center" py="$6">
                    尚無成員
                  </Text>
                ) : (
                  members.map((m, i) => (
                    <XStack
                      key={m.customer_id ?? i}
                      ai="center"
                      gap="$3"
                      px="$3.5"
                      py="$3"
                      bbw={1}
                      bbc={v2.divider}
                      cursor="pointer"
                      pressStyle={{ opacity: 0.7 }}
                      onPress={() => setEditingIndex(i)}>
                      <Avatar name={m.customer_name} color={m.color} />
                      <YStack f={1} miw={0} gap="$1">
                        <XStack ai="center" gap="$2" fw="wrap">
                          <Text fontSize={14} fontWeight="700" color={v2.title}>
                            {m.customer_name}
                          </Text>
                          <RoleTag label={roleLabel(m)} tone={isOwner(m) ? "amber" : "primary"} />
                        </XStack>
                      </YStack>
                      <RiArrowRightSLine size={18} color={v2.sub} />
                    </XStack>
                  ))
                )}
                {/* 邀請入口：與帳戶明細的「新增地址」同一款（卡內置中的文字列），
                    不再是虛線外框方塊——虛線框在 V2 只留給「可以撕下來給人」的邀請碼卡。 */}
                <XStack
                  ai="center"
                  jc="center"
                  gap="$1.5"
                  py="$3"
                  btw={1}
                  btc={v2.divider}
                  cursor="pointer"
                  pressStyle={{ opacity: 0.6 }}
                  onPress={goInvite}>
                  <RiAddLine size={15} color={v2.primary} />
                  <Text fontSize={13} fontWeight="700" color={v2.primary}>
                    邀請新成員
                  </Text>
                </XStack>
              </ListContainer>
            </YStack>

            {/* 陌生訪客自動加入（只有負責人）：付款連結付完款的人申請加入時直接生效或要核准 */}
            {viewerIsOwner && guestAutoJoin !== null && (
              <YStack>
                <SectionLabel tone="dark" title="加入設定" />
                <ListContainer>
                  <ToggleRow
                    icon={<RiUserAddLine size={15} color={colors.primary} />}
                    iconColor={colors.primary}
                    label="陌生訪客自動加入"
                    sub="用付款連結付完款的人可以申請加入這個家庭。打開：直接加入；關閉：要你核准。"
                    value={guestAutoJoin}
                    onChange={toggleGuestAutoJoin}
                    isLast
                  />
                </ListContainer>
              </YStack>
            )}

            <InfoNote tone="primary">點選成員可查看資料；負責人可移除其他成員。</InfoNote>

            {/* TODO(account-hide): 角色說明 / 負責人轉讓 暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
          </>
        ) : (
          editing && (
            <>
              {/* 成員標頭 */}
              <ListContainer p="$3.5">
                <XStack ai="center" gap="$3">
                  <Avatar name={editing.customer_name} color={editing.color} size={44} />
                  <YStack f={1} miw={0} gap="$1">
                    <Text fontSize={16} fontWeight="800" color={v2.title}>
                      {editing.customer_name}
                    </Text>
                    <XStack ai="center" gap="$2">
                      <RoleTag label={roleLabel(editing)} tone={isOwner(editing) ? "amber" : "primary"} />
                    </XStack>
                  </YStack>
                </XStack>
              </ListContainer>

              {/* TODO(account-hide): 快速套用角色 / 各分類權限 尚未接 API，暫時隱藏。詳見 docs/account-hidden-sections.md */}

              {/* 移除成員（僅負責人可操作；負責人本身不可被移除） */}
              {viewerIsOwner && !isOwner(editing) && (
                <ListContainer>
                  <FnRow
                    icon={<RiDeleteBin6Line size={15} color={colors.danger} />}
                    iconColor={colors.danger}
                    label={`從帳戶移除「${editing.customer_name}」`}
                    sub="移除後將立即失去此帳戶所有權限"
                    danger
                    isLast
                    onPress={() => setRemoveTarget(editingIndex)}
                  />
                </ListContainer>
              )}
            </>
          )
        )}
      </YStack>

      {/* 移除成員確認 BottomSheet */}
      <BottomSheet open={removeTarget !== null} onClose={closeRemove}>
        <YStack px="$4" pb="$5" gap="$3">
          <YStack ai="center" gap="$2" py="$2">
            <Avatar name={removingMember?.customer_name ?? ""} color={removingMember?.color} size={56} />
            <Text fontSize={18} fontWeight="800" color={v2.title} ta="center">
              確定移除 {removingMember?.customer_name}？
            </Text>
            <Text fontSize={12} color={v2.sub} ta="center">
              移除後對方將立即失去「{groupName}」的所有權限，需重新邀請才能再加入。
            </Text>
          </YStack>
          <XStack gap="$3">
            <XStack
              f={1}
              ai="center"
              jc="center"
              py="$3"
              br={999}
              bg={v2.chipBg}
              cursor={removing ? "default" : "pointer"}
              opacity={removing ? 0.5 : 1}
              pressStyle={{ opacity: 0.7 }}
              onPress={closeRemove}>
              <Text fontSize={14} fontWeight="700" color={v2.sub}>
                取消
              </Text>
            </XStack>
            <XStack
              f={1}
              ai="center"
              jc="center"
              py="$3"
              br={999}
              bg={colors.danger}
              cursor={removing ? "default" : "pointer"}
              opacity={removing ? 0.6 : 1}
              pressStyle={{ opacity: 0.85 }}
              onPress={removing ? undefined : confirmRemove}>
              <Text fontSize={14} fontWeight="700" color={colors.light}>
                {removing ? "移除中…" : "確定移除"}
              </Text>
            </XStack>
          </XStack>
        </YStack>
      </BottomSheet>

      {/* 拒絕申請確認 BottomSheet */}
      <BottomSheet open={rejectTarget !== null} onClose={closeReject}>
        <YStack px="$4" pb="$5" gap="$3">
          <YStack ai="center" gap="$2" py="$2">
            <Avatar name={rejectTarget?.customer_name ?? ""} size={56} />
            <Text fontSize={18} fontWeight="800" color={v2.title} ta="center">
              拒絕 {rejectTarget?.customer_name} 的申請？
            </Text>
            <Text fontSize={12} color={v2.sub} ta="center">
              拒絕後對方不會加入「{groupName}」。
            </Text>
          </YStack>
          <XStack gap="$3">
            <XStack
              f={1}
              ai="center"
              jc="center"
              py="$3"
              br={999}
              bg={v2.chipBg}
              cursor={actingId !== null ? "default" : "pointer"}
              opacity={actingId !== null ? 0.5 : 1}
              pressStyle={{ opacity: 0.7 }}
              role="button"
              onPress={closeReject}>
              <Text fontSize={14} fontWeight="700" color={v2.sub}>
                取消
              </Text>
            </XStack>
            <XStack
              f={1}
              ai="center"
              jc="center"
              py="$3"
              br={999}
              bg={colors.danger}
              cursor={actingId !== null ? "default" : "pointer"}
              opacity={actingId !== null ? 0.6 : 1}
              pressStyle={{ opacity: 0.85 }}
              role="button"
              onPress={actingId !== null ? undefined : confirmReject}>
              <Text fontSize={14} fontWeight="700" color={colors.light}>
                {actingId !== null ? "處理中…" : "確定拒絕"}
              </Text>
            </XStack>
          </XStack>
        </YStack>
      </BottomSheet>
    </SkinV2Screen>
  );
};

export default MemberPermissions;
